import { Chip } from "@/components/sync/EndpointCard";
import { utc } from "@/components/sync/syncText";
import { AlertTriangle, Info, XCircle } from "lucide-react";

const KINDS = [["funds", "Funds & margins"], ["holdings", "Holdings"], ["positions", "Positions"]];
const ICON = { error: XCircle, warning: AlertTriangle, info: Info };
const TONE = { error: "text-rose-300", warning: "text-amber-300", info: "text-sky-300" };

function KindCard({ kind, label, s }) {
  return (
    <div data-testid={`rp-card-${kind}`} className="rounded-md border border-[var(--border-subtle)] bg-[var(--surface)] p-3 space-y-1 text-[11px] text-slate-400">
      <div className="flex items-center justify-between"><span className="text-xs font-semibold text-slate-200">{label}</span><Chip status={s?.status} testId={`rp-card-${kind}-status`} /></div>
      {kind === "positions" && <div className="text-[10px] text-sky-300">UNVERIFIED — not validated on real non-empty data</div>}
      {kind === "funds" && <div className="text-[10px] text-sky-300">Data received ≠ financially verified</div>}
      <div className="grid grid-cols-2 gap-x-2">
        <span>Items</span><span data-testid={`rp-card-${kind}-items`} className="font-mono text-right">{s ? s.item_count : "—"}</span>
        <span>Freshness</span><span data-testid={`rp-card-${kind}-fresh`} className="font-mono text-right">{s ? s.freshness.state : "no snapshot"}</span>
        <span>Captured</span><span className="font-mono text-right">{utc(s?.captured_at)}</span>
        <span>Source sync</span><span data-testid={`rp-card-${kind}-run`} className="font-mono text-right truncate" title={s?.run_id}>{s?.run_id || "—"}</span>
        <span>Broker time</span><span className="font-mono text-right">{s?.broker_ts || "not supplied"}</span>
      </div>
    </div>
  );
}

export function SummaryCards({ s }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-2">{KINDS.map(([k, l]) => <KindCard key={k} kind={k} label={l} s={s.snapshots[k]} />)}</div>;
}

export function ActionList({ actions }) {
  return (
    <ul data-testid="rp-actions" className="space-y-1">
      {actions.map((a) => {
        const I = ICON[a.severity];
        return <li key={a.code} data-testid={`rp-action-${a.code}`} className={`flex items-start gap-2 text-[11px] ${TONE[a.severity]}`}><I className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a.message}</li>;
      })}
    </ul>
  );
}

export function SeparationPanel({ s }) {
  const p = s.separate_portfolios;
  const r = s.reconciliation;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-slate-400">
      <div data-testid="rp-recon" className="rounded-md border border-[var(--border-subtle)] p-3 space-y-1">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-200">Holdings reconciliation <Chip status={r.status} testId="rp-recon-status" /></div>
        <div>Advisory only · ledger changes: {r.ledger_changes} · rows: {r.summary.total ?? 0}</div>
        {r.flags.length > 0 && <div className="text-amber-300">Flags: {r.flags.join(", ")}</div>}
      </div>
      <div data-testid="rp-separation" className="rounded-md border border-dashed border-[var(--border-subtle)] p-3 space-y-1">
        <div className="text-xs font-semibold text-slate-200">Not part of the real account</div>
        <div>Demo holdings: <span data-testid="rp-demo-count" className="font-mono">{p.demo_holdings}</span> · unlabelled: {p.unlabelled_holdings} · paper open positions: <span data-testid="rp-paper-count" className="font-mono">{p.paper_open_positions}</span></div>
        <div className="text-[10px] text-slate-500">Never mixed into real figures and never changed by sync or reconciliation.</div>
      </div>
    </div>
  );
}
